import { request } from './client';
import type { CreditSpendListResponse } from '../types';

// Types for billing API
export interface CreditsSummary {
  totalCredits: number;
  usedCredits: number;
  availableCredits: number;
}

export interface GiftCreditsInput {
  amount: number;
  reason: string;
}

export interface GiftCreditsResponse {
  credits: CreditsSummary;
  message: string;
  creditsGifted: number;
  newCreditBalance: number;
}

export interface PaymentSettings {
  mode: 'test' | 'live';
  freeCreditsForNewUsers: number;
  googlePayEnabled: boolean;
  applePayEnabled: boolean;
  subscriptionsEnabled: boolean;
}

export interface PaymentSettingsResponse {
  settings: PaymentSettings;
  canSwitchMode: boolean;
}

export interface PublicPaymentConfig {
  publishableKey: string;
  mode: 'test' | 'live';
  googlePayEnabled: boolean;
  applePayEnabled: boolean;
  subscriptionsEnabled: boolean;
  freeCreditsForNewUsers: number;
}

// User billing endpoints
export const billingApi = {
  // Get current user's credits
  async getMyCredits(): Promise<CreditsSummary> {
    return request<CreditsSummary>('/api/squareup/billing/credits');
  },

  // Get current user's credit spending history (paginated)
  async getMyCreditSpends(limit = 20, offset = 0): Promise<CreditSpendListResponse> {
    return request<CreditSpendListResponse>(
      `/api/squareup/billing/credit-spends?limit=${limit}&offset=${offset}`
    );
  },
};

// Admin billing endpoints
export const adminBillingApi = {
  // Get user's credits (admin)
  async getUserCredits(userId: string): Promise<CreditsSummary> {
    return request<CreditsSummary>(`/api/squareup/admin/users/${userId}/credits`);
  },

  // Gift credits to a user (admin)
  async giftCredits(userId: string, input: GiftCreditsInput): Promise<GiftCreditsResponse> {
    return request<GiftCreditsResponse>(`/api/squareup/admin/users/${userId}/credits`, {
      method: 'POST',
      body: input,
    });
  },

  // Get payment settings (admin)
  async getPaymentSettings(): Promise<PaymentSettingsResponse> {
    return request<PaymentSettingsResponse>('/api/squareup/admin/payment-settings');
  },

  // Update payment settings (admin)
  async updatePaymentSettings(settings: Partial<Omit<PaymentSettings, 'mode'>>): Promise<PaymentSettings> {
    return request<PaymentSettings>('/api/squareup/admin/payment-settings', {
      method: 'PUT',
      body: settings,
    });
  },

  // Switch payment mode (super admin only)
  async switchPaymentMode(mode: 'test' | 'live'): Promise<PaymentSettings> {
    return request<PaymentSettings>('/api/squareup/admin/payment-settings/mode', {
      method: 'POST',
      body: { mode },
    });
  },
};

// Public endpoints (no auth required)
export const publicPaymentApi = {
  async getPublicConfig(): Promise<PublicPaymentConfig> {
    return request<PublicPaymentConfig>('/api/squareup/payment-settings/public');
  },
};
